// A body's IAU pole and prime meridian at an instant: the registry row's own
// secular terms, plus the periodic terms of the bodies whose model carries them.

import { J2000_JD } from './clock.ts';
import { DAYS_PER_CENTURY } from './ephemeris.ts';
import type { BodyRow } from './types.ts';

/** ICRF pole and the prime meridian east of the equator's ICRF node, degrees. */
export interface PoleAndMeridian {
  raDeg: number;
  decDeg: number;
  /** Null where the row publishes no prime meridian or no spin rate. */
  wDeg: number | null;
}

/** One argument E_i: its value at J2000 and rate per day, then its RA (sin), Dec (cos) and W (sin) amplitudes, all degrees. */
type PeriodicTerm = readonly [e0: number, ratePerDay: number, ra: number, dec: number, w: number];

interface PeriodicModel {
  readonly terms: readonly PeriodicTerm[];
  /** W's quadratic term, degrees per day squared. */
  readonly wQuad: number;
}

// Kept here rather than on the registry row because canon's RotationBlock fixes
// the row's rotation fields; these are the published model's own constants.
const PERIODIC_TERMS: Readonly<Record<string, PeriodicModel>> = {
  // IAU WGCCRE 2009 (Archinal et al. 2011, Table 2), which the 2015 report leaves
  // standing for the Moon; as carried in NAIF pck00011.tpc.
  luna: {
    terms: [
      [125.045, -0.0529921, -3.8787, 1.5419, 3.5610],
      [250.089, -0.1059842, -0.1204, 0.0239, 0.1208],
      [260.008, 13.0120009, 0.0700, -0.0278, -0.0642],
      [176.625, 13.3407154, -0.0172, 0.0068, 0.0158],
      [357.529, 0.9856003, 0, 0, 0.0252],
      [311.589, 26.4057084, 0.0072, -0.0029, -0.0066],
      [134.963, 13.0649930, 0, 0.0009, -0.0047],
      [276.617, 0.3287146, 0, 0, -0.0046],
      [34.226, 1.7484877, 0, 0, 0.0028],
      [15.134, -0.1589763, -0.0052, 0.0008, 0.0052],
      [119.743, 0.0036096, 0, 0, 0.0040],
      [239.961, 0.1643573, 0, 0, 0.0019],
      [25.053, 12.9590088, 0.0043, -0.0009, -0.0044],
    ],
    wQuad: -1.4e-12,
  },
};

const DEG = Math.PI / 180;

/** False where the row publishes no pole, and `out` is left alone. */
export function poleAndMeridianAt(row: BodyRow, jdTdb: number, out: PoleAndMeridian): boolean {
  const r = row.rotation;
  if (r.pole_ra_deg === null || r.pole_dec_deg === null) return false;
  const d = jdTdb - J2000_JD;
  const T = d / DAYS_PER_CENTURY;
  out.raDeg = r.pole_ra_deg + r.pole_rates.ra_deg_per_cy * T;
  out.decDeg = r.pole_dec_deg + r.pole_rates.dec_deg_per_cy * T;
  out.wDeg = r.prime_meridian_deg !== null && r.W_rate_deg_day !== null
    ? r.prime_meridian_deg + r.W_rate_deg_day * d
    : null;
  const model = PERIODIC_TERMS[row.id];
  if (model === undefined) return true;
  let ra = 0;
  let dec = 0;
  let w = model.wQuad * d * d;
  for (const [e0, rate, aRa, aDec, aW] of model.terms) {
    const e = (e0 + rate * d) * DEG;
    const s = Math.sin(e);
    ra += aRa * s;
    dec += aDec * Math.cos(e);
    w += aW * s;
  }
  out.raDeg += ra;
  out.decDeg += dec;
  if (out.wDeg !== null) out.wDeg += w;
  return true;
}
